import * as React from 'react';
import {
  ActivityIndicator,
  Image,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';

import PrimaryButton from '../../../../../shared/buttons/PrimaryButton';
import DefaultOutlineButton from '../../../../../shared/buttons/DefaultOutlineButton';
import LabelWithOption from '../../../../../shared/forms/LabelWithOption';
import CoverPicker from '../../../../../shared/image_picker/CoverPicker';
import DimensionContext from '../../../../../shared/dimension/DimensionContext';
import AlertModal from '../../../../../shared/modals/AlertModal';
import MaterialRightCheck from '../../../../../shared/MaterialRightCheck';
import ImageCrop from '../../../../../shared/image_crop/ImageCrop';

import File from '../../../../../../../domain/entities/File';

interface Props {
  image: File | null;
  submitting?: boolean;
  onChangeImage: (file: File) => void;
  onSubmit: (file: File) => void;
  onRequestReset: () => void;
}

const Form: React.FC<Props> = props => {
  const {image, submitting, onChangeImage, onSubmit, onRequestReset} = props;
  const imageCropRef = React.useRef<ImageCrop>(null);
  const [imageSize, setImageSize] = React.useState<{
    width: number;
    height: number;
  } | null>(null);
  const [confirmedMaterialRight, setConfirmedMaterialRight] =
    React.useState(false);
  React.useEffect(() => {
    if (image?.uri) {
      Image.getSize(image.uri, (width, height) => {
        setImageSize({width, height});
      });
    }
  }, [image]);
  const handlePressConfirmedMaterialRight = React.useCallback(() => {
    setConfirmedMaterialRight(!confirmedMaterialRight);
  }, [confirmedMaterialRight]);
  const [imagePickerError, setImagePickerError] = React.useState<string | null>(
    null,
  );
  const handleRequestReset = React.useCallback(() => {
    setConfirmedMaterialRight(false);
    setImageSize(null);
    onRequestReset();
  }, [onRequestReset]);
  const handleSubmit = React.useCallback(() => {
    if (imageCropRef.current && image) {
      imageCropRef.current.crop(image.name).then(onSubmit);
    }
  }, [imageCropRef.current, onChangeImage, onSubmit, image]);
  const onCloseModal = React.useCallback(() => {
    setImagePickerError(null);
  }, []);
  return (
    <View style={styles.container}>
      <View style={styles.thumbnailWrapper}>
        <>
          <View style={styles.labelWrapper}>
            <LabelWithOption title={'背景'} />
            <DefaultOutlineButton
              style={styles.resetButton}
              fontSizeClass={'small'}
              onPress={handleRequestReset}>
              リセット
            </DefaultOutlineButton>
          </View>
          <DimensionContext.Consumer>
            {context => {
              const width = context.content.width - MARGIN_HORIZONTAL * 2 - 50;
              const height = width / ASPECT_RATIO;
              return (
                <View style={{width, height}}>
                  {!image ? (
                    <CoverPicker
                      style={{height, width}}
                      source={null}
                      noImageText={'背景なし'}
                      recommendedSize={`${IMAGE_WIDTH}×${IMAGE_HEIGHT}`}
                      onChangeImage={onChangeImage}
                      onFailImage={setImagePickerError}
                    />
                  ) : (
                    <>
                      {imageSize ? (
                        <View style={{alignItems: 'center'}}>
                          <ImageCrop
                            ref={imageCropRef}
                            uri={image.uri}
                            imageWidth={imageSize.width}
                            imageHeight={imageSize.height}
                            cropHeight={width / ASPECT_RATIO}
                            cropWidth={width}
                            pixelRatio={3}
                            zoom={1}
                          />
                        </View>
                      ) : (
                        <View
                          style={{
                            width,
                            height,
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}>
                          <ActivityIndicator />
                        </View>
                      )}
                    </>
                  )}
                </View>
              );
            }}
          </DimensionContext.Consumer>
        </>
      </View>
      {image && (
        <View style={{marginTop: 16}}>
          <MaterialRightCheck
            checked={confirmedMaterialRight}
            onPress={handlePressConfirmedMaterialRight}
          />
        </View>
      )}
      <View style={styles.button}>
        <PrimaryButton
          disabled={!confirmedMaterialRight || submitting}
          onPress={handleSubmit}>
          {submitting ? 'アップロード中...' : '決定'}
        </PrimaryButton>
      </View>
      <AlertModal visible={!!imagePickerError} onCloseModal={onCloseModal}>
        {imagePickerError}
      </AlertModal>
    </View>
  );
};

export default React.memo(Form);

const IMAGE_WIDTH = 2480;
const IMAGE_HEIGHT = 2015;

const ASPECT_RATIO = IMAGE_WIDTH / IMAGE_HEIGHT;

const MARGIN_HORIZONTAL = 16;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginVertical: 60,
  } as ViewStyle,
  thumbnailWrapper: {
    flex: 1,
    alignItems: 'center',
  } as ViewStyle,
  labelWrapper: {
    flexDirection: 'row',
    height: 30,
    alignSelf: 'stretch',
    marginBottom: 14,
    marginHorizontal: MARGIN_HORIZONTAL,
  } as ViewStyle,
  button: {
    alignItems: 'center',
    paddingVertical: 20,
  } as ViewStyle,
  resetButton: {
    borderRadius: 24,
    width: 84,
    height: 32,
    marginHorizontal: 4,
  } as ViewStyle,
});
